import * as React from 'react'

import {
  i18n,
  SocialShareRenderer,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import {
  AuthorInformation,
  AuthorDetails,
} from '@thg-commerce/gravity-elements'
import {
  RelatedArticles,
  FeaturedArticles,
} from '@thg-commerce/gravity-patterns'
import { GridItem } from '@thg-commerce/gravity-system'
import { AuthorDetailsProps } from '@thg-commerce/gravity-elements/AuthorDetails/AuthorDetails'
import { AuthorInformationProps } from '@thg-commerce/gravity-elements/AuthorInformation/AuthorInformation'
import { SocialShareRendererProps } from '@thg-commerce/enterprise-core/src/SocialShareRenderer/SocialShareRenderer'
import { RelatedArticlesProps } from '@thg-commerce/gravity-patterns/RelatedArticles/RelatedArticles'
import { FeaturedArticlesProps } from '@thg-commerce/gravity-patterns/FeaturedArticles/FeaturedArticles'
import {
  Breadcrumbs,
  BreadcrumbItem,
} from '@thg-commerce/gravity-elements/Breadcrumbs/Breadcrumbs'
import { spacing } from '@thg-commerce/enterprise-theme'

import {
  SocialsGridItem,
  StyledSocialShareRenderer,
  StyledGrid,
  FooterSocialShareGridItem,
  AuthorInformationGridItem,
  RelatedArticlesGridItem,
  ReferencesGridItem,
  ArticleBodyGridItem,
} from './style'
import { References, Signoff } from '..'
import { ReferencesProps } from '../References/References'
import { WidgetData } from '../../types'
import { ArticleBodyRenderer } from '../../renderer/ArticleBodyRenderer'
import { ArticleContextProvider } from '../ArticleContextProvider'
import { PostContent } from '../../../post-content'

export interface ArticleProps {
  isAmp: boolean
  authorDetails: AuthorDetailsProps
  socialShareRenderer?: SocialShareRendererProps
  content: PostContent
  authorInformation: AuthorInformationProps
  featuredArticles?: FeaturedArticlesProps
  relatedArticles?: RelatedArticlesProps
  references?: ReferencesProps
  postTemplate: string
  tesseractWidgetData: WidgetData
  breadcrumbs: {
    enabled: boolean
    items: BreadcrumbItem[]
  }
  signoff?: string
}

export const Article = ({
  isAmp,
  authorDetails,
  socialShareRenderer,
  content,
  authorInformation,
  relatedArticles,
  featuredArticles,
  references,
  breadcrumbs,
  signoff,
}: ArticleProps) => {
  let row = 0
  const getRow = () => (row = row + 1)
  const breadcrumbsAriaLabel = i18n('general.breadcrumbs.arialabel')
  const { imageServiceURL } = useSiteDefinition()

  relatedArticles?.articles.forEach((article) => {
    const postImage = article.image.src[0].url
    const fullImagePath = postImage.replace(/^.+\/(?=app|wp-content)/g, '')
    // Only replace image url if we were able to replace the pattern
    if (fullImagePath !== postImage) {
      article.image.src[0].url = `${imageServiceURL}/xsmall/original/${fullImagePath}`
    }
  })

  return (
    <ArticleContextProvider productSkus={content.head?.productSkus || []}>
      {breadcrumbs.enabled && (
        <GridItem rowStart={getRow()} colSpan={12} colStart={1}>
          <Breadcrumbs
            breadcrumbLabel={breadcrumbsAriaLabel}
            items={breadcrumbs.items}
          />
        </GridItem>
      )}
      <GridItem
        style={{ marginBottom: spacing(5) }}
        colSpan={[12, 10, 8, 6]}
        colStart={[1, 2, 3, 4]}
        rowStart={getRow()}
      >
        <StyledGrid>
          <GridItem
            colSpan={socialShareRenderer ? [12, 12, 6, 6] : 12}
            colStart={1}
            rowStart={1}
          >
            <AuthorDetails {...authorDetails} />
          </GridItem>
          {socialShareRenderer && (
            <SocialsGridItem
              colSpan={[12, 12, 6, 6]}
              colStart={[1, 1, 7, 7]}
              rowStart={[2, 2, 1, 1]}
            >
              <StyledSocialShareRenderer {...socialShareRenderer} />
            </SocialsGridItem>
          )}
        </StyledGrid>
      </GridItem>
      <ArticleBodyGridItem colSpan={12} colStart={1} rowStart={getRow()}>
        <ArticleBodyRenderer
          isAmp={isAmp}
          content={content}
          insetGridItem={{
            colStart: [1, 2, 3, 4],
            colSpan: [12, 10, 8, 6],
          }}
        />
      </ArticleBodyGridItem>
      {references && (
        <ReferencesGridItem
          colSpan={[12, 10, 8, 6]}
          colStart={[1, 2, 3, 4]}
          rowStart={getRow()}
          data-testid="article-references-griditem"
        >
          <References isAmp={references.isAmp} content={references.content} />
        </ReferencesGridItem>
      )}
      {signoff && (
        <GridItem
          colSpan={[12, 10, 8, 6]}
          colStart={[1, 2, 3, 4]}
          rowStart={getRow()}
          data-testid="article-signoff-griditem"
        >
          <Signoff isAmp={isAmp} content={signoff} />
        </GridItem>
      )}
      {featuredArticles && (
        <GridItem
          colSpan={[12, 10, 8, 6]}
          colStart={[1, 2, 3, 4]}
          rowStart={getRow()}
        >
          <FeaturedArticles {...featuredArticles} />
        </GridItem>
      )}
      {socialShareRenderer && (
        <FooterSocialShareGridItem
          colSpan={12}
          colStart={1}
          rowStart={getRow()}
        >
          <SocialShareRenderer {...socialShareRenderer} />
        </FooterSocialShareGridItem>
      )}
      <AuthorInformationGridItem
        colSpan={[12, 10, 8, 6]}
        colStart={[1, 2, 3, 4]}
        rowStart={getRow()}
      >
        <AuthorInformation {...authorInformation} />
      </AuthorInformationGridItem>
      {relatedArticles && (
        <RelatedArticlesGridItem
          colSpan={[12, 10, 10, 10]}
          colStart={[1, 2, 2, 2]}
          rowStart={getRow()}
        >
          <RelatedArticles {...relatedArticles} />
        </RelatedArticlesGridItem>
      )}
    </ArticleContextProvider>
  )
}
